import React, { FC, useCallback, useContext, useState } from 'react';
import Button from '@mui/material/Button';
import { UserContext } from '../../contexts/UserContext';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { apiLicense } from '../../resources/api/apiLicense';
import { useDialog } from '../../hooks/useDialog';
import { Link, useHistory } from 'react-router-dom';
import { RequestOfflineLicenseStandalone } from '../RequestOfflineLicenseStandalone/RequestOfflineLicenseStandalone';
import { requestFileExtension } from '../../const/airgap';
import { LicensesContext } from '../../contexts/LicensesContext';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { ELicenseMode } from '../../models/ILicense';

const { useGenerateAirgap, useGenerateAirgapLegacy, useGetPixotopeVersion } = apiLicense;

export const RequestAirgapButton: FC = () => {
  const { userId } = useContext(UserContext);
  const { organization } = useContext(OrganizationContext);
  const handleError = useApiErrorHandler();

  const { licenses, onRefresh } = useContext(LicensesContext);

  const { Guard, execute: generateAirgap } = useGenerateAirgap(organization ?? void 0, userId ?? void 0);
  const { execute: generateAirgapLegacy } = useGenerateAirgapLegacy(organization ?? void 0, userId ?? void 0);

  const { execute: getVersion } = useGetPixotopeVersion(organization ?? void 0, userId ?? void 0);

  const { isOpen, openUrl, close } = useDialog('offline');

  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = useCallback(
    async (file: File, basePackages: number[], addonPackages: number[]) => {
      try {
        setIsLoading(true);
        if (file.name.endsWith(requestFileExtension)) {
          await generateAirgap(file, basePackages, addonPackages);
        } else {
          await generateAirgapLegacy(file, basePackages[0]);
        }
        await onRefresh();

        const search = new URLSearchParams(history.location.search);
        search.delete('dialog');
        search.set('license', basePackages[0].toString());
        search.set('action', 'download');
        search.set('mode', ELicenseMode.Airgap);
        // wait for 2 seconds that server can update license file status
        await new Promise((resolve) => setTimeout(resolve, 2000));
        history.push(`${history.location.pathname}?${search.toString()}`);
      } catch (e) {
        handleError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [generateAirgap, generateAirgapLegacy, onRefresh, history, handleError]
  );

  const getPxVersion = useCallback(async (file: File) => {
    return await getVersion(file);
  }, []);

  if (!licenses || !userId) return null;

  return (
    <Guard>
      <Button variant={'outlined'} color={'primary'} component={Link} to={openUrl}>
        Request offline license
      </Button>
      <RequestOfflineLicenseStandalone
        open={isOpen}
        onClose={close}
        licenses={licenses}
        onSubmit={onSubmit}
        getPixotopeVersion={getPxVersion}
        isLoading={isLoading}
      />
    </Guard>
  );
};
